import { useEffect, useState } from "react";
import { Boxes, Loader2, Check, RotateCcw, Trash2 } from "lucide-react";
import { adminApi } from "../lib/adminAuth";

const FILTERS = [
  { key: "backlog", label: "Pending" },
  { key: "added", label: "Added" },
  { key: "all", label: "All" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

/**
 * CoverageBacklogWidget — review queue for medications admins flagged for
 * coverage. Reads the separate `medication_coverage_backlog` collection and
 * lets admins mark items as added, reopen, or remove. Never touches the live
 * medications catalog.
 */
export default function CoverageBacklogWidget() {
  const [filter, setFilter] = useState("backlog");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // item id currently being mutated

  const load = (f) => {
    setLoading(true);
    setError("");
    return adminApi()
      .get(`/events/coverage-backlog?status=${f}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi()
      .get(`/events/coverage-backlog?status=${filter}`)
      .then(({ data }) => { if (!cancelled) { setData(data); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter]);

  const act = async (item, verb) => {
    setBusy(item.id);
    try {
      if (verb === "delete") {
        await adminApi().delete(`/events/coverage-backlog/${item.id}`);
      } else {
        await adminApi().post(`/events/coverage-backlog/${item.id}/${verb}`);
      }
      await load(filter);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section data-testid="coverage-backlog-widget" className="mt-12">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500">
          <Boxes className="h-3.5 w-3.5 text-stone-600" />
          Coverage backlog
          {data && (
            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
              {data.pending_count} pending
            </span>
          )}
        </h2>
        <div className="inline-flex rounded-lg border border-stone-200 bg-white p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`coverage-backlog-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                filter === f.key ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-stone-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading backlog…</span>
          </div>
        ) : error ? (
          <p data-testid="coverage-backlog-error" className="py-4 text-sm text-red-600">
            Failed to load: {error}
          </p>
        ) : !data?.has_data ? (
          <p data-testid="coverage-backlog-empty" className="py-8 text-center text-sm font-medium text-stone-600">
            {filter === "added"
              ? "Nothing marked as added yet."
              : "No medications in the backlog. Add them from the “Top requested medications” widget above."}
          </p>
        ) : (
          <>
            <p className="mb-3 text-[11px] text-stone-400">
              {data.pending_count} pending · {data.added_count} added.
            </p>
            <div className="overflow-x-auto">
              <table data-testid="coverage-backlog-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                    <th className="py-2 pr-3 font-medium">Medication / term</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 text-right font-medium">Added to queue</th>
                    <th className="py-2 pl-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const isAdded = row.status === "added";
                    return (
                      <tr
                        key={row.id}
                        data-testid={`coverage-backlog-row-${row.normalized}`}
                        className="border-b border-stone-100 last:border-0"
                      >
                        <td className="py-2.5 pr-3 font-medium capitalize text-stone-800">{row.term}</td>
                        <td className="px-3 py-2.5">
                          {isAdded ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                              <Check className="h-3 w-3" /> Added
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-stone-500">{fmtDate(row.created_at)}</td>
                        <td className="py-2.5 pl-3 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            {isAdded ? (
                              <button
                                type="button"
                                data-testid={`coverage-backlog-reopen-${row.normalized}`}
                                onClick={() => act(row, "reopen")}
                                disabled={busy === row.id}
                                className="inline-flex items-center gap-1 rounded-md border border-stone-200 px-2 py-1 text-[11px] font-medium text-stone-600 transition hover:border-stone-900 hover:text-stone-900 disabled:opacity-50"
                              >
                                {busy === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                                Reopen
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-testid={`coverage-backlog-mark-added-${row.normalized}`}
                                onClick={() => act(row, "mark-added")}
                                disabled={busy === row.id}
                                className="inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-[11px] font-medium text-emerald-700 transition hover:border-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                              >
                                {busy === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                Mark added
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`coverage-backlog-delete-${row.normalized}`}
                              onClick={() => act(row, "delete")}
                              disabled={busy === row.id}
                              className="inline-flex items-center justify-center rounded-md border border-stone-200 p-1 text-stone-400 transition hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                              aria-label="Remove from backlog"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
